import { useEffect, useState } from "react";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import ConfirmDeleteModal from "../../components/confirmDeleteModal";
import { useToast } from "../../hooks/useToast";
import { API_BASE } from "../../config/apiConfig";
import { FaEdit, FaTrash, FaBars, FaCheck, FaTimes } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import MiniNavigationTabs from "./miniNavigationTabs";

export default function ManageGuidelinesPage() {
  const token = sessionStorage.getItem("access_token");

  const [guidelines, setGuidelines] = useState([]);
  const [newGuideline, setNewGuideline] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const [reorderMode, setReorderMode] = useState(false);

  const { showToast, ToastContainer } = useToast();

  // ------------------------------------------------------------
  // FETCH
  // ------------------------------------------------------------
  async function fetchGuidelines() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/guidelines-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        // Ensure order field exists
        const sorted = data.guidelines
          .map((g, idx) => ({ ...g, order: g.order ?? idx + 1 }))
          .sort((a, b) => a.order - b.order);

        setGuidelines(sorted);
      } else {
        showToast(data.message || "Failed to load guidelines", "error");
      }
    } catch {
      showToast("Server error", "error");
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchGuidelines();
  }, []);

  // ------------------------------------------------------------
  // ADD
  // ------------------------------------------------------------
  async function handleAdd(e) {
    e.preventDefault();
    if (!newGuideline.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/guidelines-create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: newGuideline }),
      });

      const data = await res.json();

      if (data.success) {
        showToast("Guideline added!", "success");
        setNewGuideline("");
        fetchGuidelines();
      } else {
        showToast(data.message || "Failed to add guideline", "error");
      }
    } catch {
      showToast("Server error", "error");
    }
    setLoading(false);
  }

  // ------------------------------------------------------------
  // DELETE
  // ------------------------------------------------------------
  async function handleDelete() {
    if (!deleteId) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/guidelines-delete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: deleteId }),
      });

      const data = await res.json();

      if (data.success) {
        showToast("Guideline deleted", "success");
        setDeleteId(null);
        fetchGuidelines();
      } else {
        showToast(data.message || "Failed to delete", "error");
      }
    } catch {
      showToast("Server error", "error");
    }
    setLoading(false);
  }

  // ------------------------------------------------------------
  // EDIT
  // ------------------------------------------------------------
  async function handleConfirmEdit(id) {
    if (!editText.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/guidelines-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, text: editText }),
      });

      const data = await res.json();

      if (data.success) {
        showToast("Guideline updated!", "success");
        setEditingId(null);
        setEditText("");
        fetchGuidelines();
      } else {
        showToast(data.message || "Update failed", "error");
      }
    } catch {
      showToast("Server error", "error");
    }

    setLoading(false);
  }

  // ------------------------------------------------------------
  // REORDER
  // ------------------------------------------------------------
  async function handleReorder(result) {
    if (!result.destination) return;

    const items = Array.from(guidelines);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    // assign new orders
    const reordered = items.map((g, idx) => ({ ...g, order: idx + 1 }));
    setGuidelines(reordered);

    // send to backend
    await fetch(`${API_BASE}/api/guidelines-reorder`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: reordered.map((g) => ({ id: g.id, order: g.order })),
      }),
    });
  }

  return (
    <MainContentLayout>
      <PageHeader title="Manage Guidelines" backTo="/dashboard" />
      <MiniNavigationTabs />

      <ToastContainer />
      <LoadingOverlay show={loading} />

      {/* Add Form */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-6 w-full max-w-3xl mx-auto">
        <input
          type="text"
          value={newGuideline}
          onChange={(e) => setNewGuideline(e.target.value)}
          placeholder="Enter a new guideline..."
          className="border px-3 py-2 rounded flex-grow placeholder:italic placeholder:text-gray-400"
        />
        <button className="px-4 bg-blue-600 text-white rounded">Add</button>
      </form>

      {/* Reorder Mode Toggle */}
      <div className="w-full max-w-4xl mx-auto mb-4 flex justify-end">
        <button
          onClick={() => setReorderMode(!reorderMode)}
          className={`px-4 py-2 rounded text-white shadow-md transition ${
            reorderMode ? "bg-orange-500 hover:bg-orange-600" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {reorderMode ? "Finish Reordering" : "Enable Reorder Mode"}
        </button>
      </div>

      {/* Guidelines list */}
      <DragDropContext onDragEnd={handleReorder}>
        <Droppable droppableId="guidelines">
          {(provided) => (
            <div
              className="w-full max-w-4xl mx-auto space-y-3"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {guidelines.length === 0 ? (
                <p>No guidelines added yet.</p>
              ) : (
                guidelines.map((g, index) => (
                  <Draggable
                    key={g.id}
                    draggableId={g.id}
                    index={index}
                    isDragDisabled={!reorderMode}
                  >
                    {(provided, snapshot) => (
                      <div
                        className={`flex items-center gap-4 border p-3 rounded bg-white shadow-sm w-full transition-all duration-200 ${
                          snapshot.isDragging ? "bg-blue-50 scale-[1.02]" : ""
                        }`}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        {/* Drag Handle + Index */}
                        <div className="flex items-center gap-4">
                          {reorderMode && (
                            <div
                              {...provided.dragHandleProps}
                              className="text-gray-500 cursor-grab active:cursor-grabbing"
                            >
                              <FaBars />
                            </div>
                          )}

                          <span className="font-medium w-6 text-gray-500">
                            {index + 1}.
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-grow">
                          {editingId === g.id ? (
                            <input
                              className="border px-2 py-1 rounded w-full bg-white"
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                            />
                          ) : (
                            <div className="flex items-center gap-3 flex-grow text-left">
                              <span className="text-left">{g.text}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4 ml-4">
                          {editingId === g.id ? (
                            <>
                              <button
                                onClick={() => handleConfirmEdit(g.id)}
                                className="text-green-600"
                              >
                                <FaCheck size={18} />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingId(null);
                                  setEditText("");
                                }}
                                className="text-gray-500"
                              >
                                <FaTimes size={18} />
                              </button>
                            </>
                          ) : (
                            !reorderMode && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingId(g.id);
                                    setEditText(g.text);
                                  }}
                                  className="text-blue-600"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  onClick={() => setDeleteId(g.id)}
                                  className="text-red-600"
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {deleteId && (
        <ConfirmDeleteModal
          show={true}
          title="Delete Guideline"
          message="Are you sure you want to delete this guideline?"
          onCancel={() => setDeleteId(null)}
          onConfirm={handleDelete}
        />
      )}
    </MainContentLayout>
  );
}
